import { DataTable } from '@/components/features/shared/DataTable'
import { Summary } from '@/components/features/shared/Summary'
import { downloadCsv, formatNumEsAr } from '@/components/features/shared/utils'
import { Alert, AlertTitle } from '@/components/ui/alert'
import { Button, IconButton } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { SegmentedControl, SegmentedControlItem } from '@/components/ui/segmented-control'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CollectorAction, CollectorBillingPeriodData } from '@/domain/collectors/collector'
import { BillingType, InvoiceData } from '@/domain/invoices/invoice'
import { useAssuredPeopleVerify } from '@/hooks/api/useAssuredPeople'
import { collectorKeys, useCollectorPayments, useCollectors, useModifyPaymentsByCollector } from '@/hooks/api/useCollectors'
import { useOpenDocument } from '@/hooks/api/useDocuments'
import { useAgreementInvoices } from '@/hooks/api/useInvoices'
import { useOfferings } from '@/hooks/api/useOfferings'
import { toast } from '@/hooks/use-toast'
import { useFrontendPagination } from '@/hooks/useFrontendPagination'
import { Paths } from '@/routes/paths'
import { convertToDigitStr } from '@/utils/helpers'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { ArrowLeft, Check, Download, FileText as FileInvoice, Loader2, ReceiptText as Receipt, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { PaginationState } from '@tanstack/react-table'
import { billingPeriodsSummary, createBillingPeriodColumns, formatPaymentConcept } from './CollectorManagementPage.shared'
import { formatCurrency } from '@/components/features/shared/utils'

interface CollectorsSearch {
  collectorId?: string
  periodStart?: string
  periodEnd?: string
  status: string
  serviceType: string
  offeringId?: string
  page?: number
  pageSize?: number
}

const NONE_SELECTION = 'none'

export const CollectorManagementPage = () => {
  const searchParams = useSearch({ strict: false }) as CollectorsSearch
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: currentYear - 1989 }, (_, i) => String(1990 + i))

  const [selectedCollector, setSelectedCollector] = useState(searchParams.collectorId ?? '')
  const [selectedStartMonth, setSelectedStartMonth] = useState(() => {
    if (searchParams.periodStart) {
      const parts = searchParams.periodStart.split('-')
      return (parseInt(parts[1]) - 1).toString()
    }
    return new Date().getMonth().toString()
  })
  const [selectedStartYear, setSelectedStartYear] = useState(searchParams.periodStart
    ? (() => { const parts = searchParams.periodStart.split('-'); return parseInt(parts[0]).toString() })()
    : currentYear.toString()
  )
  const [searchDates, setSearchDates] = useState({
    periodStart: searchParams.periodStart ?? '',
    periodEnd: searchParams.periodEnd ?? '',
  })
  const [selectedEndMonth, setSelectedEndMonth] = useState(() => {
    if (searchParams.periodEnd) {
      const parts = searchParams.periodEnd.split('-')
      return (parseInt(parts[1]) - 1).toString()
    }
    return new Date().getMonth().toString()
  })
  const [selectedEndYear, setSelectedEndYear] = useState(searchParams.periodEnd
    ? (() => { const parts = searchParams.periodEnd.split('-'); return parseInt(parts[0]).toString() })()
    : currentYear.toString()
  )
  const [selectedStatus, setSelectedStatus] = useState(searchParams.status ?? 'all')
  const [selectedServiceType, setSelectedServiceType] = useState(searchParams.serviceType ?? 'all')
  const [selectedOfferingId, setSelectedOfferingId] = useState(searchParams.offeringId ?? '')
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set())
  const [billingType, setBillingType] = useState(BillingType.Receipt)
  const [step, setStep] = useState<1 | 2>(1)
  const hasValidCollector = selectedCollector !== NONE_SELECTION && !!selectedCollector
  const [selectedAction, setSelectedAction] = useState<CollectorAction | typeof NONE_SELECTION>(NONE_SELECTION)

  const updateQueryParams = (newParams: Record<string, any>) => {
    navigate({
      search: (old: any) => ({ ...old, ...newParams }),
      to: Paths.Management.Collectors,
      replace: true,
      searchAs: true,
    } as any)
  }

  const { data: collectorData = [], isLoading: isLoadingCollectors } = useCollectors()
  const collectors = collectorData

  const { data: offeringsData = [] } = useOfferings({ limit: 1000 })
  const offerings = offeringsData as any[]

  const { data: paginatedData, isLoading: isLoadingBilling } = useCollectorPayments({
    collectorId: selectedCollector === NONE_SELECTION ? '' : selectedCollector,
    periodStart: searchDates.periodStart,
    periodEnd: searchDates.periodEnd,
    limit: 1000,
    status: selectedStatus,
    serviceType: selectedServiceType,
    offeringId: selectedOfferingId || undefined,
  })

  const isLoading = isLoadingCollectors || isLoadingBilling
  const selectedPaymentIds = useMemo(() => Array.from(selectedRowIds), [selectedRowIds])
  const { data: agreementInvoices } = useAgreementInvoices(selectedPaymentIds, { enabled: step === 2 && selectedPaymentIds.length > 0 })
  const { mutate: openDocumentMutate } = useOpenDocument()


  const collectorSelected = collectors?.find(c => c.id === selectedCollector)
  const collectorLabel = `${collectorSelected?.code} - ${collectorSelected?.name}`

  const invoicesByPaymentId = useMemo(() => {
    const map = new Map<string, InvoiceData[]>()
    if (agreementInvoices) {
      for (const inv of agreementInvoices) {
        const existing = map.get(inv.paymentId) || []
        existing.push(inv)
        map.set(inv.paymentId, existing)
      }
    }
    return map
  }, [agreementInvoices])

  const handleOpenDocument = ({ documentId, fileName }: { documentId: string; fileName: string }) => {
    openDocumentMutate({ documentId, fileName })
  }

  const billingPeriods: CollectorBillingPeriodData[] = paginatedData?.data || []

  const {
    paginatedData: paginatedBillingPeriods,
    pagination,
    setPagination,
    pageCount,
    totalRecords,
  } = useFrontendPagination(billingPeriods, { initialPageSize: 50, initialSearchParams: searchParams as unknown as Record<string, string | string[]> })

  useEffect(() => {
    const urlPage = searchParams.page !== undefined ? Number(searchParams.page) : undefined
    const urlPageSize = searchParams.pageSize !== undefined ? Number(searchParams.pageSize) : undefined
    if (urlPage !== undefined && !isNaN(urlPage) && urlPage !== pagination.pageIndex) {
      setPagination({ pageIndex: urlPage, pageSize: pagination.pageSize })
    }
    if (urlPageSize !== undefined && !isNaN(urlPageSize) && urlPageSize !== pagination.pageSize) {
      setPagination({ pageIndex: pagination.pageIndex, pageSize: urlPageSize })
    }
  }, [searchParams.page, searchParams.pageSize])

  const handlePaginationChange = useCallback((updater: PaginationState | ((prev: PaginationState) => PaginationState)) => {
    const newPagination = typeof updater === 'function' ? updater(pagination) : updater
    setPagination(newPagination)
    updateQueryParams({
      page: newPagination.pageIndex,
      pageSize: newPagination.pageSize,
    })
  }, [pagination, updateQueryParams])

  const selectedBillingPeriods = useMemo(() => billingPeriods.filter(bp => selectedRowIds.has(bp.paymentId)), [billingPeriods, selectedRowIds])
  const {
    paginatedData: paginatedStep2BillingPeriods,
    pagination: step2Pagination,
    setPagination: setStep2Pagination,
    pageCount: step2PageCount,
    totalRecords: step2TotalRecords,
  } = useFrontendPagination(selectedBillingPeriods, { initialPageSize: 50 })

  const selectedContractIds = useMemo(() => Array.from(new Set(selectedBillingPeriods.map(bp => bp.contractId).filter(Boolean))), [selectedBillingPeriods])
  const { data: contractsNeedingReviewData } = useAssuredPeopleVerify(selectedContractIds)

  const contractsNeedingReview = useMemo(() => {
    const map = new Map<string, { name: string; dateOfBirth: Date }>()
    if (contractsNeedingReviewData) {
      for (const cp of contractsNeedingReviewData) {
        map.set(cp.contractId, { name: cp.name, dateOfBirth: cp.dateOfBirth })
      }
    }
    return map
  }, [contractsNeedingReviewData])

  const rowSelection = useMemo(() => {
    const selection: Record<string, boolean> = {}
    billingPeriods.forEach(bp => {
      if (selectedRowIds.has(bp.paymentId)) {
        selection[bp.paymentId] = true
      }
    })
    return selection
  }, [billingPeriods, selectedRowIds])

  const handleRowSelectionChange = (selection: Record<string, boolean>) => {
    if (Object.keys(selection).length === 0) {
      return setSelectedRowIds(new Set())
    }

    const isSelectAllAcrossAllPages =
      Object.keys(selection).length > pagination.pageSize ||
      (Object.keys(selection).length === pagination.pageSize &&
        pagination.pageSize > 0 &&
        Object.values(selection).every(v => v === true) &&
        selectedRowIds.size < billingPeriods.length)

    if (isSelectAllAcrossAllPages) {
      const newSelected = new Set<string>()
      billingPeriods.forEach(bp => newSelected.add(bp.paymentId))
      setSelectedRowIds(newSelected)
      return
    }

    const contractToPaymentIds = new Map<string, Set<string>>()
    billingPeriods.forEach(bp => {
      if (!contractToPaymentIds.has(bp.contractId)) {
        contractToPaymentIds.set(bp.contractId, new Set())
      }
      contractToPaymentIds.get(bp.contractId)!.add(bp.paymentId)
    })

    const changedContractIds = new Set<string>()
    for (const paymentId of Object.keys(selection)) {
      const bp = billingPeriods.find(b => b.paymentId === paymentId)
      if (bp) {
        changedContractIds.add(bp.contractId)
      }
    }

    const newSelected = new Set(selectedRowIds)

    if (selectedRowIds.size === 0) {
      for (const contractId of changedContractIds) {
        const paymentIds = contractToPaymentIds.get(contractId)
        if (paymentIds) {
          for (const pid of paymentIds) {
            newSelected.add(pid)
          }
        }
      }
    } else {
      for (const paymentId of Object.keys(selection)) {
        const isSelected = selection[paymentId] === true
        const bp = billingPeriods.find(b => b.paymentId === paymentId)
        if (!bp) continue

        const contractPaymentIds = contractToPaymentIds.get(bp.contractId)
        if (!contractPaymentIds) continue

        for (const pid of contractPaymentIds) {
          if (isSelected) {
            newSelected.add(pid)
          } else {
            newSelected.delete(pid)
          }
        }
      }
    }

    setSelectedRowIds(newSelected)
  }

  const assignPaymentsMutation = useModifyPaymentsByCollector()

  const handleProceedToStep2 = () => {
    // console.log(selectedAction)
    // if (selectedAction === CollectorAction.PRINT_INVOICES) {
    //   const invalidPayments = billingPeriods.filter(bp => selectedRowIds.has(bp.paymentId) && bp.paymentStatus === PaymentStatus.PENDING)
    //   if (invalidPayments.length > 0) {
    //     toast({
    //       title: 'Error',
    //       description: `Solo se pueden seleccionar pagos pendientes. ${invalidPayments.length} pago(s) no es(son) pendiente(s)`,
    //       variant: 'destructive',
    //     })
    //     setSelectedAction(NONE_SELECTION)
    //     return
    //   }
    // }
    setStep(2)
  }

  const getActionLabel = (action: CollectorAction | typeof NONE_SELECTION): string => {
    switch (action) {
      case CollectorAction.TO_COLLECTOR:
        return 'asignar los pagos seleccionados al cobrador'
      case CollectorAction.FROM_COLLECTOR:
        return 'devolver los pagos seleccionados por el cobrador'
      case CollectorAction.PAYED_COLLECTOR:
        return 'rendir los pagos seleccionados por el cobrador'
      case CollectorAction.PRINT_INVOICES:
        return 'imprimir los comprobantes seleccionados'
      default:
        return ''
    }
  }

  const getActionVariant = (action: CollectorAction | typeof NONE_SELECTION): 'warning' | 'destructive' | 'success' | 'default' => {
    switch (action) {
      case CollectorAction.TO_COLLECTOR:
        return 'warning'
      case CollectorAction.FROM_COLLECTOR:
        return 'destructive'
      default:
        return 'success'
    }
  }

  const getActionFileName = (action: CollectorAction | typeof NONE_SELECTION): string => {
    switch (action) {
      case CollectorAction.TO_COLLECTOR:
        return 'Asignacion'
      case CollectorAction.FROM_COLLECTOR:
        return 'Devolucion'
      case CollectorAction.PRINT_INVOICES:
        return 'ImpresionComprobantes'
      default:
        return 'Rendicion'
    }
  }

  const handleConfirmAction = () => {
    const actionLabel = getActionLabel(selectedAction)
    const isPrintInvoices = selectedAction === CollectorAction.PRINT_INVOICES
    const collectorName = isPrintInvoices ? '' : (selectedCollector === NONE_SELECTION ? 'sin cobrador' : collectorLabel)
    const collectorPhrase = isPrintInvoices ? '' : ` ${collectorName}`
    if (window.confirm(`Se requiere confirmación. ¿Desea ${actionLabel}${collectorPhrase} por $${convertToDigitStr(summary.selectedAmount)}?`)) {
      handleCollectorAction(selectedAction as CollectorAction)
    }
  }

  const handleCollectorAction = (operation: CollectorAction) => {
    const paymentIds = Array.from(selectedRowIds)
    assignPaymentsMutation.mutateAsync({
      collectorId: selectedCollector,
      paymentIds,
      operation,
      billingType,
    }).then((result: any) => {
      if (result.invoiceCount) {
        toast({
          title: 'Operacion exitosa',
          description: `Se han procesado ${result.invoiceCount} comprobantes`,
          variant: 'success',
        })
      }
      setStep(1) // Reset to step 1 after success
      if (result?.jobId)
        navigate({
          to: `${Paths.Management.CollectorInvoices}/${selectedCollector}`,
          search: { jobId: result.jobId },
          state: { fromCollectorsSearch: searchParams },
        } as any)
    })
  }

  const handleDownloadCsv = () => {
    const actionStr = getActionFileName(selectedAction)

    const collectorName = collectorSelected ? `${collectorSelected.code}_${collectorSelected.name.replace(/\s+/g, '_')}` : selectedCollector
    const selectedData = billingPeriods.filter(bp => selectedRowIds.has(bp.paymentId))
    const today = new Date().toLocaleDateString('es-AR')
    const headers = ['Cliente', 'Nombre', 'Telefono', 'Direccion', 'Parcela', 'Contrato', 'Codigo', 'Servicio', 'Concepto', 'Importe', 'Observaciones', 'Fallecido']

    const rows = selectedData.map(bp => {
      const cliente = bp.clientCode || ''
      const nombre = `"${bp.clientName || ''}"`
      const telefono = bp.clientPhone || ''
      const direccion = bp.clientAddress || ''
      const parcela = bp.parcelSection ? `${bp.parcelSection}-${bp.parcelFila || ''}-${bp.parcela || ''}` : ''
      const contrato = bp.contractNumber || ''
      const codigo = bp.offeringCode || ''
      const servicio = `"${bp.offeringName || ''}"`
      const concepto = `"${formatPaymentConcept(bp.source, bp.description, bp.periodStart, bp.billingFrequency)}"`
      const importe = bp.totalAmount
      const reviewWarning = contractsNeedingReview.has(bp.contractId) ? `Hijo > 18 años` : ''
      const observaciones = reviewWarning
      const fallecido = `"${bp.deceasedNames || ''}"`

      return [cliente, nombre, telefono, direccion, parcela, contrato, codigo, servicio, concepto, formatNumEsAr(importe), observaciones, fallecido].join(';')
    })

    // Create summary table grouped by offeringCode
    const summaryData: Record<string, { offeringName: string, count: number, total: number }> = {}
    selectedData.forEach(bp => {
      const code = bp.offeringCode || 'N/A'
      if (!summaryData[code]) {
        summaryData[code] = { offeringName: bp.offeringName || '', count: 0, total: 0 }
      }
      summaryData[code].count += 1
      summaryData[code].total += bp.totalAmount
    })

    const summaryHeaders = ['\n\nCodigo', 'Servicio', 'Cantidad', 'Total']
    const summaryRows = Object.entries(summaryData).map(([code, data]) => {
      return [code, `"${data.offeringName}"`, data.count, formatNumEsAr(data.total)].join(';')
    })

    const totalQty = Object.values(summaryData).reduce((sum, d) => sum + d.count, 0)
    const totalSum = Object.values(summaryData).reduce((sum, d) => sum + d.total, 0)
    const totalRow = ['', '"TOTAL"', totalQty, formatNumEsAr(totalSum)].join(';')

    const csvContent = [headers.join(';'), ...rows, summaryHeaders.join(';'), ...summaryRows, totalRow].join('\n')
    downloadCsv(csvContent, `${actionStr}_${collectorName}_${today.replace(/\//g, '-')}.csv`)
  }

  const handleSearch = async () => {
    if (!selectedStartYear) return
    const { periodStart, periodEnd } = getDateRange(selectedStartMonth, selectedStartYear, selectedEndMonth, selectedEndYear)
    setSearchDates({ periodStart, periodEnd })
    setPagination({ pageIndex: 0, pageSize: 50 })
    updateQueryParams({
      collectorId: selectedCollector,
      periodStart,
      periodEnd,
      serviceType: selectedServiceType,
      page: '0',
      pageSize: '50',
    })
    await queryClient.invalidateQueries({
      queryKey: collectorKeys.billingPeriods({ collectorId: selectedCollector, periodStart, periodEnd, page: 1, limit: 99999, status: selectedStatus, serviceType: selectedServiceType, offeringId: selectedOfferingId || undefined }),
    })
  }

  const isSearchDisabled = !selectedStartYear

  const handleClearFilters = () => {
    setSelectedCollector('')
    setSelectedStartMonth(new Date().getMonth().toString())
    setSelectedStartYear(currentYear.toString())
    setSelectedEndMonth(new Date().getMonth().toString())
    setSelectedEndYear(currentYear.toString())
    setSelectedStatus('all')
    setSelectedServiceType('all')
    setSelectedOfferingId('')
    setPagination({ pageIndex: 0, pageSize: 50 })
    setSearchDates({ periodStart: '', periodEnd: '' })
    setSelectedRowIds(new Set())
    updateQueryParams({
      collectorId: '',
      periodStart: '',
      periodEnd: '',
      status: 'all',
      serviceType: 'all',
      offeringId: '',
      page: '0',
      pageSize: '50',
    })
  }

  const summary = billingPeriodsSummary(billingPeriods, selectedRowIds)

  const handleEdit = (contractId: string) => {
    navigate({
      to: '/app/management/contrataciones' + `/${contractId}`,
      search: (prev: any) => ({
        ...prev,
        collectorId: searchParams.collectorId,
        periodStart: searchParams.periodStart,
        periodEnd: searchParams.periodEnd,
      }),
    } as any)
  }

  const columns = createBillingPeriodColumns(handleEdit, true, undefined, undefined, undefined, undefined, contractsNeedingReview)

  const months = [
    { value: '0', label: 'Enero' },
    { value: '1', label: 'Febrero' },
    { value: '2', label: 'Marzo' },
    { value: '3', label: 'Abril' },
    { value: '4', label: 'Mayo' },
    { value: '5', label: 'Junio' },
    { value: '6', label: 'Julio' },
    { value: '7', label: 'Agosto' },
    { value: '8', label: 'Septiembre' },
    { value: '9', label: 'Octubre' },
    { value: '10', label: 'Noviembre' },
    { value: '11', label: 'Diciembre' },
  ]

  const getDateRange = (startMonth: string, startYear: string, endMonth: string, endYear: string) => {
    const startDate = new Date(parseInt(startYear), parseInt(startMonth), 1)
    const endDate = new Date(parseInt(endYear), parseInt(endMonth) + 1, 0)
    return {
      periodStart: `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-01`,
      periodEnd: `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`,
    }
  }

  const handleCollectorChange = (value: string) => {
    setSelectedCollector(value)
    setSelectedAction(NONE_SELECTION)
    setPagination({ pageIndex: 0, pageSize: 50 })
    setSelectedRowIds(new Set())
    setStep(1)
    updateQueryParams({ collectorId: value, page: '0', pageSize: '50' })
  }

  const handleMonthChange = (value: string) => {
    setSelectedStartMonth(value)
    setSelectedRowIds(new Set())
    const { periodStart, periodEnd } = getDateRange(value, selectedStartYear, selectedEndMonth, selectedEndYear)
    updateQueryParams({ periodStart, periodEnd, offeringId: selectedOfferingId })
  }

  const handleYearChange = (value: string) => {
    setSelectedStartYear(value)
    setSelectedRowIds(new Set())
    const { periodStart, periodEnd } = getDateRange(selectedStartMonth, value, selectedEndMonth, selectedEndYear)
    updateQueryParams({ periodStart, periodEnd, offeringId: selectedOfferingId })
    queryClient.invalidateQueries({
      queryKey: collectorKeys.billingPeriods({ collectorId: selectedCollector, periodStart, periodEnd, page: 1, limit: 99999, status: selectedStatus, serviceType: selectedServiceType, offeringId: selectedOfferingId || undefined }),
    })
  }

  const handleEndMonthChange = (value: string) => {
    setSelectedEndMonth(value)
    setSelectedRowIds(new Set())
    const { periodStart, periodEnd } = getDateRange(selectedStartMonth, selectedStartYear, value, selectedEndYear)
    updateQueryParams({ periodStart, periodEnd, offeringId: selectedOfferingId })
  }

  const handleEndYearChange = (value: string) => {
    setSelectedEndYear(value)
    setSelectedRowIds(new Set())
    const { periodStart, periodEnd } = getDateRange(selectedStartMonth, selectedStartYear, selectedEndMonth, value)
    updateQueryParams({ periodStart, periodEnd, offeringId: selectedOfferingId })
    queryClient.invalidateQueries({
      queryKey: collectorKeys.billingPeriods({ collectorId: selectedCollector, periodStart, periodEnd, page: 1, limit: 99999, status: selectedStatus, serviceType: selectedServiceType, offeringId: selectedOfferingId || undefined }),
    })
  }

  const handleOfferingChange = (value: string) => {
    setSelectedOfferingId(value)
    setSelectedRowIds(new Set())
    if (searchDates.periodStart && selectedCollector) {
      queryClient.invalidateQueries({
        queryKey: collectorKeys.billingPeriods({ collectorId: selectedCollector, periodStart: searchDates.periodStart, periodEnd: searchDates.periodEnd, page: 1, limit: 99999, status: selectedStatus, serviceType: selectedServiceType, offeringId: value || undefined }),
      })
    }
  }

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value)
    setPagination({ pageIndex: 0, pageSize: 50 })
    setSelectedRowIds(new Set())
    updateQueryParams({ status: value, offeringId: selectedOfferingId, page: '0', pageSize: '50' })
    if (searchDates.periodStart && selectedCollector) {
      queryClient.invalidateQueries({
        queryKey: collectorKeys.billingPeriods({ collectorId: selectedCollector, periodStart: searchDates.periodStart, periodEnd: searchDates.periodEnd, page: 1, limit: 99999, status: value, serviceType: selectedServiceType, offeringId: selectedOfferingId || undefined }),
      })
    }
  }

  const handleServiceTypeChange = (value: string) => {
    setSelectedServiceType(value)
    setPagination({ pageIndex: 0, pageSize: 50 })
    setSelectedRowIds(new Set())
    updateQueryParams({ serviceType: value, offeringId: selectedOfferingId, page: '0', pageSize: '50' })
    if (searchDates.periodStart && selectedCollector) {
      queryClient.invalidateQueries({
        queryKey: collectorKeys.billingPeriods({ collectorId: selectedCollector, periodStart: searchDates.periodStart, periodEnd: searchDates.periodEnd, page: 1, limit: 99999, status: selectedStatus, serviceType: value, offeringId: selectedOfferingId || undefined }),
      })
    }
  }

  const statusOptions = [
    { value: 'all', label: 'Todos' },
    { value: 'pending', label: 'Pendiente' },
    { value: 'in_collector', label: 'En cobrador' },
    { value: 'completed', label: 'Pagado' },
    { value: 'refunded', label: 'Reembolsado' },
    // { value: 'partial', label: 'Parcial' },
    { value: 'cancelled', label: 'Cancelado' },
  ]

  const serviceTypeOptions = [
    { value: 'all', label: 'Todos' },
    { value: 'abonos', label: 'Abonos' },
    { value: 'parcelas', label: 'Parcelas' },
    { value: 'cremaciones', label: 'Cremaciones' },
  ]

  const renderFilters = () => (
    <Card>
      <CardContent className="pt-6">
        <div className="grid grid-cols-12 gap-5">
          {/* Row 1: Collector | Status | Dates (row-span-2) | Buttons */}
          {/* Row 2: Service Type | Service */}

          <div className="col-span-4 flex flex-col gap-y-2">
            <Label>Cobrador</Label>
            <Select value={selectedCollector} onValueChange={handleCollectorChange} disabled={isLoadingCollectors}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccione un cobrador" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_SELECTION}>Sin cobrador</SelectItem>
                {collectors.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.code} - {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2 flex flex-col gap-y-2">
            <Label>Estado</Label>
            <Select value={selectedStatus} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-5 row-span-2 flex flex-col gap-y-3">
            <Label>Rango de fechas</Label>
            <div className="flex items-end gap-2">
              <div className="flex flex-col gap-y-1 flex-1">
                <Label className="font-mono text-[10px] text-muted-foreground leading-none ml-1">
                  Inicio
                </Label>
                <Select value={selectedStartMonth} onValueChange={handleMonthChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Mes" />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map(m => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={selectedStartYear} onValueChange={handleYearChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Año" />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map(y => (
                      <SelectItem key={y} value={y}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="pb-1">
                <div className="w-px h-full bg-border" />
              </div>

              <div className="flex flex-col gap-y-1 flex-1">
                <Label className="font-mono text-[10px] text-muted-foreground leading-none ml-1">
                  Fin
                </Label>
                <Select value={selectedEndMonth} onValueChange={handleEndMonthChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Mes" />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map(m => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={selectedEndYear} onValueChange={handleEndYearChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Año" />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map(y => (
                      <SelectItem key={y} value={y}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="col-span-1 row-span-2 flex flex-col gap-y-2">
            <IconButton onClick={handleClearFilters} variant="default" title="Limpiar filtros" className="w-full">
              <X className="h-4 w-4" />
            </IconButton>
            <Button onClick={handleSearch} variant="info" disabled={isSearchDisabled} className="w-full mt-auto">Buscar</Button>
          </div>

          <div className="col-span-4 flex flex-col gap-y-2">
            <Label>Tipo de Servicio</Label>
            <Select value={selectedServiceType} onValueChange={handleServiceTypeChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Tipo de servicio" />
              </SelectTrigger>
              <SelectContent>
                {serviceTypeOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2 flex flex-col gap-y-2">
            <Label>Servicio</Label>
            <Select value={selectedOfferingId || 'all'} onValueChange={(val) => handleOfferingChange(val === 'all' ? '' : val)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {offerings.filter(o => o.serviceType === selectedServiceType || selectedServiceType === 'all').map(o => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.code} - {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  )

  const renderResultSummary = () => (
    <Summary
      items={[
        { label: 'Total Períodos', count: summary.total, amount: summary.totalAmount },
        { label: 'Pendiente', count: summary.pendingCount, amount: summary.pendingAmount, variant: 'destructive' },
        { label: 'En Cobrador', count: summary.inCollectorCount, amount: summary.inCollectorAmount, variant: 'info' },
        { label: 'Cobrado', count: summary.completedCount, amount: summary.completedAmount, variant: 'success' },
      ]}
    />
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Gestión de Cobranza</h1>
          <p className="text-muted-foreground">
            Gestiona los períodos de cobranza por cobrador
          </p>
        </div>
      </div>

      {step === 1 && renderFilters()}
      {step === 1 && renderResultSummary()}

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <CardTitle className="text-lg">
              {step === 1
                ? 'Lista de Períodos de Cobranza'
                : selectedAction === CollectorAction.TO_COLLECTOR
                  ? 'Asignación de pagos'
                  : selectedAction === CollectorAction.FROM_COLLECTOR
                    ? 'Devolución de pagos'
                    : selectedAction === CollectorAction.PRINT_INVOICES
                      ? 'Impresión de comprobantes'
                      : 'Rendición de pagos'}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {selectedCollector === NONE_SELECTION ? 'Cobrador: Sin cobrador' : selectedCollector ? `Cobrador: ${collectorLabel}` : 'Seleccione un cobrador'}
            </p>
          </div>
          {step === 1 && (
            <div className="flex items-end gap-5">
              <div className="flex flex-col gap-1">
                <Label className="text-xs">Acción</Label>
                <Select value={selectedAction} onValueChange={(val) => setSelectedAction(val as any)}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder={hasValidCollector ? "Seleccione acción" : "Seleccione un cobrador"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_SELECTION}>-- Seleccionar --</SelectItem>
                    <SelectItem value={CollectorAction.TO_COLLECTOR}>Asignar pagos</SelectItem>
                    <SelectItem value={CollectorAction.FROM_COLLECTOR}>Devolver pagos</SelectItem>
                    <SelectItem value={CollectorAction.PAYED_COLLECTOR}>Rendir pagos</SelectItem>
                    <SelectItem value={CollectorAction.PRINT_INVOICES}>Imprimir comprobantes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handleProceedToStep2}
                disabled={selectedRowIds.size === 0 || selectedAction === NONE_SELECTION || (!hasValidCollector && selectedAction !== CollectorAction.PRINT_INVOICES)}
                className="mb-0.5"
                variant={"info"}
              >
                Siguiente
              </Button>
            </div>
          )}
          {step === 2 && selectedAction === CollectorAction.TO_COLLECTOR && (
            <div className="flex items-end gap-2">
              <div className="flex flex-col gap-1">
                <Label className="text-xs">Facturación</Label>
                <SegmentedControl value={billingType} onValueChange={(value) => setBillingType(value as BillingType)}>
                  <SegmentedControlItem value={BillingType.Invoice}>
                    <FileInvoice className="h-4 w-4" />
                    Factura
                  </SegmentedControlItem>
                  <SegmentedControlItem value={BillingType.Receipt}>
                    <Receipt className="h-4 w-4" />
                    Recibo
                  </SegmentedControlItem>
                </SegmentedControl>
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {isLoading && (
            <Alert variant={"default"}>
              <AlertTitle className="flex items-center mb-3">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />Cargando...
              </AlertTitle>
            </Alert>
          )}
          {!paginatedData || (paginatedData.data?.length === 0 && !isLoadingBilling) ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No se encontraron períodos de cobro</p>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {step === 1 ?
                    (selectedRowIds.size > 0
                      ? `${selectedRowIds.size} período(s) seleccionado(s) — ${formatCurrency(summary.selectedAmount)}`
                      : 'Seleccione períodos para asignar')
                    : <span>
                      Se procesarán <span className="font-semibold">{selectedRowIds.size}</span> pagos por un total de <span className="font-semibold">{formatCurrency(summary.selectedAmount)}</span>
                    </span>
                  }
                </p>
                <div className="flex items-center gap-5">
                  {step === 2 && (
                    <>
                      <Button variant="outline" onClick={() => {
                        setStep(1)
                        setSelectedAction(NONE_SELECTION)
                        setBillingType(BillingType.Invoice)
                      }}>
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Atrás
                      </Button>
                      <Button variant="outline" onClick={handleDownloadCsv} disabled={!hasValidCollector && selectedAction !== CollectorAction.PRINT_INVOICES}>
                        <Download className='mr-2 h-4 w-4' />
                        Descargar Informe
                      </Button>
                      <Button
                        onClick={handleConfirmAction}
                        disabled={assignPaymentsMutation.isPending || (!hasValidCollector && selectedAction !== CollectorAction.PRINT_INVOICES)}
                        variant={getActionVariant(selectedAction)}
                      >
                        {assignPaymentsMutation.isPending ?
                          <div className='flex items-center gap-1'>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Procesando...
                          </div>
                          : <div className='flex items-center gap-1'>
                            <Check className="mr-2 h-4 w-4" />
                            Confirmar Acción
                          </div>
                        }
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {step === 1 ? (
                <DataTable
                  columns={columns}
                  data={paginatedBillingPeriods}
                  getRowId={(row) => row.paymentId}
                  pagination={pagination}
                  onPaginationChange={handlePaginationChange}
                  pageCount={pageCount}
                  totalRecords={totalRecords}
                  rowSelection={rowSelection}
                  onRowSelectionChange={handleRowSelectionChange}
                  onRowClick={(row: CollectorBillingPeriodData) => {
                    handleRowSelectionChange({ [row.paymentId]: !selectedRowIds.has(row.paymentId) })
                  }}
                />
              ) : (
                <DataTable
                  columns={createBillingPeriodColumns(handleEdit, false, selectedAction, billingType, invoicesByPaymentId, handleOpenDocument, contractsNeedingReview)}
                  data={paginatedStep2BillingPeriods}
                  getRowId={(row) => row.paymentId}
                  pagination={step2Pagination}
                  onPaginationChange={setStep2Pagination}
                  pageCount={step2PageCount}
                  totalRecords={step2TotalRecords}
                />
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default CollectorManagementPage
